/**
 * Tenant resolution from the browser's hostname.
 *
 * On boot, parses the subdomain from `window.location.hostname` and
 * fetches the tenant branding from the API. Result is cached by
 * TanStack Query keyed on the subdomain.
 */

import { useQuery } from "@tanstack/react-query";

import { getTenantBranding } from "@starz/shared/api/sdk.gen";
import type { TenantBranding } from "@starz/shared/api/types.gen";

export function parseSubdomain(host: string): string | null {
  if (!host) return null;
  const cleaned = host.split(":")[0].toLowerCase();
  const parts = cleaned.split(".");
  if (parts.length < 2) return null;
  const first = parts[0];
  if (!first || first === "www" || first === "app" || first === "api") return null;
  return first;
}

export function currentSubdomain(): string | null {
  if (typeof window === "undefined") return null;
  return parseSubdomain(window.location.hostname);
}

export function useTenantBranding(): {
  data: TenantBranding | null;
  isLoading: boolean;
  isError: boolean;
  subdomain: string | null;
} {
  const subdomain = currentSubdomain();

  const query = useQuery({
    queryKey: ["tenant-branding", subdomain],
    enabled: !!subdomain,
    staleTime: 5 * 60_000, // 5 min
    queryFn: async () => {
      if (!subdomain) return null;
      const result = await getTenantBranding({ query: { subdomain } });
      if ("error" in result) {
        // Surface as null; the caller renders the "tenant not found" page
        return null;
      }
      return result.data;
    },
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    subdomain,
  };
}
